<template>
  <div class="instance-params p-4">
    <div v-if="loading" class="text-center p-8">
      <i class="pi pi-spinner pi-spin text-2xl text-blue-500"></i>
      <p class="mt-2 text-gray-600">Loading plan settings...</p>
    </div>

    <div v-else-if="error" class="text-center p-8">
      <i class="pi pi-exclamation-triangle text-2xl text-red-500"></i>
      <p class="mt-2 text-red-600">{{ error }}</p>
      <Button @click="loadPlanSettings" label="Retry" severity="secondary" class="mt-2" />
    </div>

    <div v-else class="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div class="param-group bg-white p-4 rounded-lg shadow-sm">
        <h3 class="text-lg font-semibold mb-3 text-blue-700">General settings</h3>
        <div class="params-list">
          <div class="param-item flex justify-between py-2 border-b border-gray-100">
            <span class="text-gray-600">Storage allocation</span>
            <div class="flex items-center">
              <InputNumber v-model="instanceData.resource_storage_gb" :min="1" :max="1000" showButtons
                buttonLayout="horizontal" size="small" class="w-24" :disabled="!isEditing" />
              <span class="ml-2">GB</span>
            </div>
          </div>
          <div class="param-item flex justify-between py-2 border-b border-gray-100">
            <span class="text-gray-600">Third party browser</span>
            <Dropdown v-model="instanceData.third_party_browser" :options="['weasis', 'horos', 'custom']"
              class="w-32" :disabled="!isEditing" />
          </div>
          <div class="param-item flex justify-between py-2 border-b border-gray-100">
            <span class="text-gray-600">Timeline type</span>
            <Dropdown v-model="instanceData.timeline_type" :options="['list', 'calendar', 'both']" class="w-32"
              :disabled="!isEditing" />
          </div>
          <div class="param-item flex justify-between py-2">
            <Button v-if="!isEditing" icon="pi pi-pencil" label="Edit Settings" @click="isEditing = true"
              severity="secondary" text />
            <div v-else class="flex gap-2">
              <Button icon="pi pi-check" label="Save" @click="saveInstanceSettings" severity="success" 
                :loading="saving" />
              <Button icon="pi pi-times" label="Cancel" @click="cancelEdit" severity="secondary" text />
            </div>
          </div>
        </div>
      </div>

      <div class="param-group bg-white p-4 rounded-lg shadow-sm">
        <div class="flex justify-between">
          <h3 class="text-lg font-semibold mb-3 text-blue-700">Feature toggles</h3>
          <div class="flex items-center">
            <Button v-if="!isEditing" icon="pi pi-pencil" label="Edit Features" @click="isEditing = true"
              severity="secondary" text size="small" />
          </div>
        </div>

        <div class="grid grid-cols-1 gap-3 mt-4">
          <div v-for="(field, index) in featureToggles" :key="index"
            class="toggle-item flex justify-between items-center py-2 px-3 hover:bg-gray-50 rounded-md">
            <span class="text-gray-700">{{ formatFeatureName(field.name) }}</span>
            <InputSwitch v-model="instanceData[field.name]" :disabled="!isEditing" />
          </div>
        </div>
      </div>

      <div class="param-group bg-white p-4 rounded-lg shadow-sm">
        <h3 class="text-lg font-semibold mb-3 text-blue-700">Document features</h3>
        <div class="grid grid-cols-1 gap-3 mt-4">
          <div v-for="(field, index) in documentFeatures" :key="index"
            class="toggle-item flex justify-between items-center py-2 px-3 hover:bg-gray-50 rounded-md">
            <span class="text-gray-700">{{ formatFeatureName(field.name) }}</span>
            <InputSwitch v-model="instanceData[field.name]" :disabled="!isEditing" />
          </div>
        </div>
      </div>

      <div class="param-group bg-white p-4 rounded-lg shadow-sm">
        <h3 class="text-lg font-semibold mb-3 text-blue-700">Workflow features</h3>
        <div class="grid grid-cols-1 gap-3 mt-4">
          <div v-for="(field, index) in workflowFeatures" :key="index"
            class="toggle-item flex justify-between items-center py-2 px-3 hover:bg-gray-50 rounded-md">
            <span class="text-gray-700">{{ formatFeatureName(field.name) }}</span>
            <InputSwitch v-model="instanceData[field.name]" :disabled="!isEditing" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, watch } from 'vue';
import axios from 'axios';
import InputSwitch from 'primevue/inputswitch';
import InputNumber from 'primevue/inputnumber';
import Dropdown from 'primevue/dropdown';
import Button from 'primevue/button';
import { useToast } from 'primevue/usetoast';

const toast = useToast();

const props = defineProps({
  tenant: { type: String, required: true
  }
});

const isEditing = ref(false);
const loading = ref(false);
const saving = ref(false);
const error = ref(null);
const originalData = ref({});

const instanceData = ref({
  resource_storage_gb: 500,
  third_party_browser: 'weasis',
  audit_reports_enabled: true,
  qr_codes_preferred: false,
  radiology_enabled: true,
  resources_enabled: true,
  emails_enabled: true,
  invoicing_enabled: true,
  register_enabled: false,
  doq_exchange_enabled: true,
  contemporaneous_notes_enabled: true,
  time_tracking_enabled: true,
  worklist_enabled: true,
  remote_backups_enabled: false,
  doq_builder_enabled: true,
  doq_builder_letter_enabled: true,
  doq_builder_report_enabled: true,
  disable_doq_exchange_download: false,
  scope_register: false,
  user_documents_enabled: true,
  publications_enabled: false,
  timeline_type: 'list',
  reminders_enabled: true,
  timeline_enabled: true,
  dashboard_enabled: true,
  forms_enabled: true,
  tasks_enabled: true,
  contacts_enabled: true,
  grammar_check_enabled: true,
  register_user_settings_enabled: true,
  radiology_schedule: true
});

const featureToggles = [
  { name: 'dashboard_enabled' },
  { name: 'audit_reports_enabled' },
  { name: 'emails_enabled' },
  { name: 'invoicing_enabled' },
  { name: 'publications_enabled' },
  { name: 'register_enabled' },
  { name: 'remote_backups_enabled' },
  { name: 'scope_register' },
  {name: "case_invitations_enabled"},
  { name: 'forms_enabled' },
  { name: 'tasks_enabled' },
  { name: 'contacts_enabled' },
  { name: 'grammar_check_enabled' },
  { name: 'register_user_settings_enabled' },
   {name: "radiology_schedule"}
];

const documentFeatures = [
  { name: 'doq_exchange_enabled' },
  { name: 'disable_doq_exchange_download' },
  { name: 'doq_builder_enabled' },
  { name: 'user_documents_enabled' }
];

const workflowFeatures = [
  { name: 'timeline_enabled' },
  { name: 'reminders_enabled' },
  { name: 'contemporaneous_notes_enabled' },
  { name: 'time_tracking_enabled' },
  { name: 'worklist_enabled' },
  { name: 'radiology_enabled' },
  { name: 'resources_enabled' },
  { name: 'qr_codes_preferred' }
];

// Load plan settings from API
const loadPlanSettings = async () => {
  if (!props.tenant) {
    error.value = 'Tenant is required to load plan settings';
    return;
  }

  loading.value = true;
  error.value = null;

  try {
    const response = await axios.get(`/api/v1/accounts_proxy/${props.tenant}/plan_settings`);
    
    if (response.data && response.data.data) {
      instanceData.value = { ...instanceData.value, ...response.data.data };
      originalData.value = JSON.parse(JSON.stringify(instanceData.value));
    }
  } catch (err) {
    console.error('Error loading plan settings:', err);
    error.value = err.response?.data?.error || 'Failed to load plan settings';
    toast.add({
      severity: 'error',  summary: 'Error', detail: error.value, life: 5000
    });
  } finally {
    loading.value = false;
  }
};

const saveInstanceSettings = async () => {
  if (!props.tenant) {
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Tenant is required to save plan settings',
      life: 5000
    });
    return;
  }

  saving.value = true;

  try {
    const response = await axios.patch(`/api/v1/accounts_proxy/${props.tenant}/plan_settings`, instanceData.value);
    
    if (response.status === 200) {
      toast.add({
        severity: 'success',
        summary: 'Settings Saved',
        detail: 'Instance settings have been updated successfully',
        life: 3000
      });
      originalData.value = JSON.parse(JSON.stringify(instanceData.value));
      isEditing.value = false;
    }
  } catch (err) {
    console.error('Error saving plan settings:', err);
    const errorMessage = err.response?.data?.error || 'Failed to save plan settings';
    toast.add({
      severity: 'error',
      summary: 'Save Failed',
      detail: errorMessage,
      life: 5000
    });
  } finally {
    saving.value = false;
  }
};

const cancelEdit = () => {
  instanceData.value = JSON.parse(JSON.stringify(originalData.value));
  
  toast.add({
    severity: 'info', summary: 'Edits Cancelled', detail: 'Changes have been discarded', life: 3000
  });
  isEditing.value = false;
};

const formatFeatureName = (name) => {
  return name
    .replace(/_/g, ' ').replace(/enabled/g, '').replace(/doq/g, 'DOQ').split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ').trim();
};

watch(() => props.tenant, (newTenant) => {
  if (newTenant) {
    loadPlanSettings();
  }
}, { immediate: true });

onMounted(() => {
  if (props.tenant) {
    loadPlanSettings();
  }
});
</script>

<style scoped>
.toggle-item {
  transition: all 0.2s ease-in-out;
}

.toggle-item:hover {
  background-color: #f8fafc;
}

:deep(.p-inputswitch.p-inputswitch-checked .p-inputswitch-slider) {
  background: #4ade80 !important;
}
</style> 